import {VehicleCommissionDataHelper} from "./VehicleCommissionDataHelper";
import {GvfClient} from "./GvfClient";
import {GvfNetworkError} from "./errors/GvfNetworkError";
import {ServiceConfigProviderV1} from "@volkswagen-onehub/service-config-provider";
import {SpecificationFilter} from "./SpecificationFilter";
import { getCustomConfig } from '../serviceConfig/ServiceConfigHelper';
import { IVumFlattenedVehicle } from '../vum/VumFlattenedVehicleConverter';

export interface IVehicle extends IVumFlattenedVehicle {
    readonly modelName?: string;
    readonly exteriorColor?: string;
    readonly engine?: string;
    readonly specifications?: string[];
    readonly modelYear?: string;
    readonly exteriorColorText?: string;
}

export class GvfService {
    private readonly vehicleCommissionDataHelper: VehicleCommissionDataHelper;
    private readonly specificationFilter: SpecificationFilter;

    constructor(private readonly gvfClient: GvfClient,
                private readonly serviceConfigProvider: ServiceConfigProviderV1) {
        const gvfCustomServiceConfig: any = serviceConfigProvider.configs['myvw_group-vehicle-file']!.customConfig;
        const validBids = gvfCustomServiceConfig.validBids;

        this.vehicleCommissionDataHelper = new VehicleCommissionDataHelper(validBids);
        this.specificationFilter = new SpecificationFilter(gvfCustomServiceConfig.prNumberOrigins);
    }

    public async getGvfEnrichedVehicle(vehicle: IVehicle, modelNameFallback: string): Promise<IVehicle> {
        if (vehicle.vin) {
            vehicle = await this.getGvfEnrichedVinVehicle(vehicle);
        } else if (vehicle.commissionNumber) {
            const commissionVehicleCombinations = this.vehicleCommissionDataHelper.getAllCommissionVehicleCombinations(vehicle);
            vehicle = await this.getGvfEnrichedCommissionDataVehicle(commissionVehicleCombinations);
        }

        if (getCustomConfig(this.serviceConfigProvider.configs['myvw_group-vehicle-file']).useFallbackForModelName) {
            return {...vehicle, modelName: modelNameFallback};
        }

        return vehicle;
    }

    private async getGvfEnrichedVinVehicle(vehicle: IVehicle): Promise<IVehicle> {
        const gvfDataResponse = await this.gvfClient.getGvfData(vehicle.vin!);
        const gvfDetailsResponse = await this.gvfClient.getGvfDetails(vehicle.vin!);
        let specifications = this.specificationFilter.filter(gvfDetailsResponse);

        return {...vehicle, ...gvfDataResponse, ...gvfDetailsResponse, specifications};
    }

    private async getGvfEnrichedCommissionDataVehicle(vehicles: IVehicle[]): Promise<IVehicle> {
        const gvfDataResponse = await this.getVehicleWithGvfDataForPossibleCombinations(vehicles);
        const gvfDetailsResponse = await this.gvfClient.getGvfDetails(gvfDataResponse.bid! + gvfDataResponse.commissionNumberYear + gvfDataResponse.commissionNumber);
        let specifications = this.specificationFilter.filter(gvfDetailsResponse);

        return {...gvfDataResponse, ...gvfDetailsResponse, specifications};
    }

    private async getVehicleWithGvfDataForPossibleCombinations(vehicles: IVehicle[]): Promise<IVehicle> {
        let lastException = new GvfNetworkError();
        let gvfResponsesWithBids = await Promise.all(vehicles.map(async (vehicle) => {
            try {
                const response = await this.gvfClient.getGvfData(vehicle.bid! + vehicle.commissionNumberYear + vehicle.commissionNumber);
                if (response) {
                    return {...vehicle, ...response} as IVehicle;
                }
            } catch (e: any) {
                lastException = e;
            }
            return undefined;
        }));

        const result = gvfResponsesWithBids.find((response) => response !== undefined);
        if (result) {
            return result;
        } else {
            throw lastException;
        }
    }
}
